import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Clock, Download, Share2, BookOpen, Tag, Calendar, User, CheckCircle, AlertCircle, Lightbulb, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const ArticleContent = ({ article }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Fonction pour ajouter une nouvelle page si nécessaire
      const checkNewPage = (neededSpace = 20) => {
        if (yPosition + neededSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // En-tête avec fond bleu
      doc.setFillColor(37, 99, 235); // Bleu
      doc.rect(0, 0, pageWidth, 45, 'F');

      // Logo/Marque
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('BOZZAM', margin, 20);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Le blog des freelances', margin, 28);

      // Catégorie
      doc.setFontSize(9);
      doc.text(article.category.toUpperCase(), pageWidth - margin - 30, 20);

      yPosition = 55;

      // Titre de l'article
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(article.title, contentWidth);
      doc.text(titleLines, margin, yPosition);
      yPosition += (titleLines.length * 10) + 5;

      // Ligne de séparation
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, margin + 40, yPosition);
      yPosition += 10;

      // Métadonnées
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const dateStr = new Date(article.publishedAt).toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
      doc.text(`Publié le ${dateStr}  •  ${article.readTime} min de lecture  •  Équipe Bozzam`, margin, yPosition);
      yPosition += 8;

      // Tags
      doc.text(`Tags: ${article.tags.join(', ')}`, margin, yPosition);
      yPosition += 12;

      // Résumé avec fond gris
      doc.setFillColor(245, 245, 245);
      const excerptLines = doc.splitTextToSize(article.excerpt, contentWidth - 10);
      const excerptHeight = (excerptLines.length * 5) + 10;
      doc.roundedRect(margin, yPosition, contentWidth, excerptHeight, 3, 3, 'F');
      
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text(excerptLines, margin + 5, yPosition + 7);
      yPosition += excerptHeight + 10;

      // Contenu de l'article - utiliser le même tokenizer que l'affichage
      const content = article.fullContent || article.content || '';
      const pdfBlocks = (() => {
        const lines = content.split('\n');
        const blks = [];
        let idx = 0;
        const getType = (l) => {
          const t = l.trim();
          if (t === '' || t === '---') return 'empty';
          if (t.startsWith('|') && t.includes('|')) return 'table';
          if (t.startsWith('## ')) return 'h2';
          if (t.startsWith('### ')) return 'h3';
          if (/^\d+\.\s/.test(t)) return 'ol';
          if (/^[-•*]\s/.test(t)) return 'ul';
          return 'text';
        };
        while (idx < lines.length) {
          const tp = getType(lines[idx]);
          if (tp === 'empty') { idx++; continue; }
          if (tp === 'table') {
            const tl = [];
            while (idx < lines.length && getType(lines[idx]) === 'table') { tl.push(lines[idx].trim()); idx++; }
            blks.push({ type: 'table', lines: tl });
          } else if (tp === 'h2') { blks.push({ type: 'h2', text: lines[idx].trim().replace(/^##\s*/, '') }); idx++; }
          else if (tp === 'h3') { blks.push({ type: 'h3', text: lines[idx].trim().replace(/^###\s*/, '') }); idx++; }
          else if (tp === 'ol') {
            const items = [];
            while (idx < lines.length && getType(lines[idx]) === 'ol') { items.push(lines[idx].trim().replace(/^\d+\.\s*/, '')); idx++; }
            blks.push({ type: 'ol', items });
          } else if (tp === 'ul') {
            const items = [];
            while (idx < lines.length && getType(lines[idx]) === 'ul') { items.push(lines[idx].trim().replace(/^[-•*]\s*/, '')); idx++; }
            blks.push({ type: 'ul', items });
          } else {
            let txt = '';
            while (idx < lines.length && getType(lines[idx]) === 'text') { txt += (txt ? '\n' : '') + lines[idx]; idx++; }
            if (txt.trim()) blks.push({ type: 'text', text: txt.trim() });
          }
        }
        return blks;
      })();

      for (const block of pdfBlocks) {
        // Tableaux
        if (block.type === 'table') {
          const parseRow = (l) => l.split('|').map(c => c.trim()).filter(c => c);
          const headers = parseRow(block.lines[0]);
          const sepIdx = block.lines.findIndex(l => /^\|?\s*[-:]+\s*\|/.test(l));
          const dataRows = sepIdx >= 0 ? block.lines.slice(sepIdx + 1).map(parseRow).filter(r => r.length > 0) : [];
          if (headers.length > 0 && dataRows.length > 0) {
            const colCount = headers.length;
            const colWidth = contentWidth / colCount;
            const rowHeight = 7;
            checkNewPage((dataRows.length + 1) * rowHeight + 15);
            yPosition += 3;
            doc.setFillColor(37, 99, 235);
            doc.rect(margin, yPosition - 5, contentWidth, rowHeight, 'F');
            doc.setTextColor(255); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
            headers.forEach((h, ci) => { doc.text(h.replace(/\*\*/g, ''), margin + ci * colWidth + 2, yPosition); });
            yPosition += rowHeight;
            dataRows.forEach((row, ri) => {
              checkNewPage(rowHeight + 5);
              if (ri % 2 === 0) { doc.setFillColor(245, 247, 250); doc.rect(margin, yPosition - 5, contentWidth, rowHeight, 'F'); }
              doc.setTextColor(60); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
              row.forEach((cell, ci) => {
                if (ci < colCount) doc.text(cell.replace(/\*\*/g, '').substring(0, Math.floor(colWidth / 2)), margin + ci * colWidth + 2, yPosition);
              });
              yPosition += rowHeight;
            });
            doc.setDrawColor(200); doc.setLineWidth(0.2);
            doc.rect(margin, yPosition - (dataRows.length + 1) * rowHeight - 5, contentWidth, (dataRows.length + 1) * rowHeight);
            yPosition += 5;
          }
          continue;
        }

        // Titres H2
        if (block.type === 'h2') {
          checkNewPage(20);
          yPosition += 5;
          doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(37, 99, 235);
          doc.text(block.text, margin, yPosition);
          yPosition += 8;
          doc.setDrawColor(37, 99, 235); doc.setLineWidth(0.3);
          doc.line(margin, yPosition, margin + doc.getTextWidth(block.text), yPosition);
          yPosition += 8;
          continue;
        }

        // Sous-titres H3
        if (block.type === 'h3') {
          checkNewPage(15);
          yPosition += 3;
          doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(50, 50, 50);
          doc.text(block.text.replace(/\*\*/g, ''), margin, yPosition);
          yPosition += 8;
          continue;
        }

        // Listes ordonnées
        if (block.type === 'ol') {
          for (let oi = 0; oi < block.items.length; oi++) {
            checkNewPage(12);
            // Cercle bleu numéroté
            doc.setFillColor(37, 99, 235);
            doc.circle(margin + 3, yPosition - 1.5, 3, 'F');
            doc.setTextColor(255); doc.setFontSize(7); doc.setFont('helvetica', 'bold');
            doc.text(String(oi + 1), margin + 1.5, yPosition - 0.3);
            // Texte
            doc.setTextColor(60); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
            const itemText = block.items[oi].replace(/\*\*/g, '');
            const itemLines = doc.splitTextToSize(itemText, contentWidth - 12);
            doc.text(itemLines, margin + 9, yPosition);
            yPosition += (itemLines.length * 5) + 3;
          }
          yPosition += 3;
          continue;
        }

        // Listes non ordonnées
        if (block.type === 'ul') {
          for (const item of block.items) {
            checkNewPage(10);
            doc.setFillColor(34, 197, 94);
            doc.circle(margin + 2, yPosition - 1.5, 1.5, 'F');
            doc.setTextColor(60); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
            const itemText = item.replace(/\*\*/g, '');
            const itemLines = doc.splitTextToSize(itemText, contentWidth - 10);
            doc.text(itemLines, margin + 7, yPosition);
            yPosition += (itemLines.length * 5) + 3;
          }
          yPosition += 3;
          continue;
        }

        // Texte normal
        const trimmed = block.text;
        checkNewPage(15);
        doc.setTextColor(60); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
        const cleanText = trimmed.replace(/\*\*/g, '');
        const textLines = doc.splitTextToSize(cleanText, contentWidth);
        doc.text(textLines, margin, yPosition);
        yPosition += (textLines.length * 5) + 5;
      }

      // Pied de page sur la dernière page
      checkNewPage(40);
      yPosition = pageHeight - 35;
      
      // Encadré CTA
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, 25, 3, 3, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Découvrez l\'application Bozzam', margin + 10, yPosition + 10);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('L\'outil tout-en-un des freelances : missions, facturation, simulateurs', margin + 10, yPosition + 17);
      
      doc.setFontSize(8);
      doc.text('app.bozzam.com', pageWidth - margin - 25, yPosition + 13);

      // Copyright
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.text('© 2026 Bozzam - Tous droits réservés - blog.bozzam.com', pageWidth / 2, pageHeight - 5, { align: 'center' });

      // Télécharger le PDF
      doc.save(`${article.slug}-bozzam.pdf`);
      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
    
    setIsGeneratingPDF(false);
  };

  const shareArticle = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Lien copié dans le presse-papier !');
    }
  };

  // Fonction pour parser un tableau markdown
  const parseMarkdownTable = (tableLines) => {
    if (tableLines.length < 2) return null;
    const parseRow = (line) => line.split('|').map(c => c.trim()).filter(c => c);
    const headers = parseRow(tableLines[0]);
    const sepIndex = tableLines.findIndex(l => /^\|?\s*[-:]+\s*\|/.test(l));
    if (sepIndex === -1) return null;
    const rows = tableLines.slice(sepIndex + 1).map(parseRow).filter(r => r.length > 0);
    return { headers, rows };
  };

  // Tokenizer intelligent : découpe le contenu en blocs typés ligne par ligne
  const tokenizeContent = (content) => {
    if (!content) return [];
    const lines = content.split('\n');
    const blocks = [];
    let i = 0;

    const getLineType = (line) => {
      const t = line.trim();
      if (t === '' || t === '---') return 'empty';
      if (t.startsWith('|') && t.includes('|')) return 'table';
      if (t.startsWith('## ')) return 'h2';
      if (t.startsWith('### ')) return 'h3';
      if (/^\d+\.\s/.test(t)) return 'ol';
      if (/^[-•*]\s/.test(t)) return 'ul';
      return 'text';
    };

    while (i < lines.length) {
      const type = getLineType(lines[i]);

      if (type === 'empty') { i++; continue; }

      if (type === 'table') {
        const tableLines = [];
        while (i < lines.length && getLineType(lines[i]) === 'table') {
          tableLines.push(lines[i].trim());
          i++;
        }
        blocks.push({ type: 'table', lines: tableLines });
        continue;
      }

      if (type === 'h2') {
        blocks.push({ type: 'h2', text: lines[i].trim().replace(/^##\s*/, '') });
        i++; continue;
      }

      if (type === 'h3') {
        blocks.push({ type: 'h3', text: lines[i].trim().replace(/^###\s*/, '') });
        i++; continue;
      }

      // Detect "Sources" block: **Sources...:** followed by bullet list
      if (type === 'text' && /^\*\*Sources?\b.*:\*\*/.test(lines[i].trim())) {
        const title = lines[i].trim().replace(/\*\*/g, '');
        i++;
        const items = [];
        while (i < lines.length) {
          const lt = getLineType(lines[i]);
          if (lt === 'ul') {
            items.push(lines[i].trim().replace(/^[-•*]\s*/, ''));
            i++;
          } else if (lt === 'empty') {
            i++; break;
          } else { break; }
        }
        blocks.push({ type: 'sources', title, items });
        continue;
      }

      if (type === 'ol') {
        const items = [];
        while (i < lines.length && getLineType(lines[i]) === 'ol') {
          items.push(lines[i].trim().replace(/^\d+\.\s*/, ''));
          i++;
        }
        blocks.push({ type: 'ol', items });
        continue;
      }

      if (type === 'ul') {
        const items = [];
        while (i < lines.length && getLineType(lines[i]) === 'ul') {
          items.push(lines[i].trim().replace(/^[-•*]\s*/, ''));
          i++;
        }
        blocks.push({ type: 'ul', items });
        continue;
      }

      // type === 'text' : collecter jusqu'au prochain type différent
      let text = '';
      while (i < lines.length) {
        const lt = getLineType(lines[i]);
        if (lt !== 'text') break;
        text += (text ? '\n' : '') + lines[i];
        i++;
      }
      if (text.trim()) blocks.push({ type: 'text', text: text.trim() });
    }
    return blocks;
  };

  // Fonction pour parser et formater le contenu de l'article (HTML)
  const formatContent = (content) => {
    if (!content) return null;
    const blocks = tokenizeContent(content);

    return blocks.map((block, index) => {
      // Tableaux
      if (block.type === 'table') {
        const table = parseMarkdownTable(block.lines);
        if (!table) {
          return <p key={index} className="text-gray-700 leading-relaxed mb-4 text-lg">{block.lines.join('\n')}</p>;
        }
        return (
          <div key={index} className="my-6 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full text-sm" data-testid={`article-table-${index}`}>
              <thead>
                <tr className="bg-blue-600 text-white">
                  {table.headers.map((h, hi) => (
                    <th key={hi} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{formatInlineText(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, ri) => (
                  <tr key={ri} className={`border-b border-gray-100 ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 ${ci === 0 ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{formatInlineText(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      // Titre H2
      if (block.type === 'h2') {
        return (
          <h2 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-4 pb-2 border-b-2 border-blue-200">
            {block.text}
          </h2>
        );
      }

      // Titre H3
      if (block.type === 'h3') {
        return (
          <h3 key={index} className="text-xl font-semibold text-gray-800 mt-8 mb-3">
            {formatInlineText(block.text)}
          </h3>
        );
      }

      // Liste ordonnée (1. 2. 3.)
      if (block.type === 'ol') {
        return (
          <ol key={index} className="my-4 space-y-2">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
                <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-gray-700 leading-relaxed">{formatInlineText(item)}</span>
              </li>
            ))}
          </ol>
        );
      }

      // Liste non ordonnée (- ou *)
      if (block.type === 'ul') {
        return (
          <ul key={index} className="my-4 space-y-2">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{formatInlineText(item)}</span>
              </li>
            ))}
          </ul>
        );
      }

      // Sources officielles (bloc dédié)
      if (block.type === 'sources') {
        return (
          <div key={index} className="my-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm" data-testid="article-sources">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-blue-600" />
              <h4 className="font-bold text-blue-900 text-lg">{block.title}</h4>
            </div>
            <ul className="space-y-2">
              {block.items.map((item, i) => {
                const parts = item.split(' : ');
                const label = parts[0];
                const rawUrl = parts.length > 1 ? parts.slice(1).join(' : ').trim() : null;
                // N'afficher en lien QUE si ça ressemble à un vrai domaine (ex: urssaf.fr, bofip.impots.gouv.fr)
                const isUrl = rawUrl && /^(https?:\/\/)?[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(\/[^\s]*)?$/i.test(rawUrl);
                const url = isUrl ? rawUrl : null;
                const suffixText = !isUrl && rawUrl ? rawUrl : null;
                return (
                  <li key={i} className="flex items-start gap-3 p-2 hover:bg-white/60 rounded-lg transition-colors">
                    <ExternalLink className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <span className="font-medium text-gray-800">{label}</span>
                      {suffixText && <span className="ml-2 text-gray-700">: {suffixText}</span>}
                      {url && (
                        <a
                          href={url.startsWith('http') ? url : `https://${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {url}
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      }

      // Bloc texte
      const trimmed = block.text;

      // Titres de section (lignes en majuscules)
      if (trimmed.match(/^[A-Z][A-Z\s\-:.'\/]+$/) && trimmed.length < 80 && trimmed.length > 5) {
        return (
          <h2 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-4 pb-2 border-b-2 border-blue-200">
            {trimmed}
          </h2>
        );
      }

      // Texte qui commence par **texte:** (encadré info)
      if (trimmed.startsWith('**') && trimmed.includes(':**')) {
        return (
          <div key={index} className="my-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
            <p className="text-gray-800">{formatInlineText(trimmed)}</p>
          </div>
        );
      }

      // Encadrés conseil
      if (trimmed.toLowerCase().startsWith('conseil') || trimmed.toLowerCase().startsWith('astuce')) {
        return (
          <div key={index} className="my-6 p-5 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
            <div className="flex items-start gap-3">
              <Lightbulb className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-800 mb-1">Conseil</p>
                <p className="text-blue-700">{formatInlineText(trimmed)}</p>
              </div>
            </div>
          </div>
        );
      }

      // Paragraphes normaux
      return (
        <p key={index} className="text-gray-700 leading-relaxed mb-4 text-lg">
          {formatInlineText(trimmed)}
        </p>
      );
    });
  };

  // Fonction pour formater le texte inline (gras, italique)
  const formatInlineText = (text) => {
    // Remplacer **texte** par du gras
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <article className="max-w-4xl mx-auto">
      {/* Header de l'article */}
      <header className="mb-10">
        {/* Catégorie */}
        <div className="mb-4">
          <Badge className="bg-blue-600 text-white text-sm px-3 py-1">
            {article.category}
          </Badge>
        </div>

        {/* Titre */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
          {article.title}
        </h1>

        {/* Résumé */}
        <p className="text-xl text-gray-600 leading-relaxed mb-6 border-l-4 border-blue-500 pl-4 italic">
          {article.excerpt}
        </p>

        {/* Métadonnées */}
        <div className="flex flex-wrap items-center gap-6 mb-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{article.readTime} min de lecture</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>Équipe Bozzam</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {article.tags.map((tag, index) => (
            <Badge key={index} variant="outline" className="text-xs bg-gray-50">
              <Tag className="h-3 w-3 mr-1" />
              {tag}
            </Badge>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pb-6 border-b border-gray-200">
          <Button onClick={shareArticle} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Share2 className="h-4 w-4 mr-2" />
            Partager l'article
          </Button>
          <Button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            variant="outline"
          >
            <Download className="h-4 w-4 mr-2" />
            {isGeneratingPDF ? 'Génération...' : 'Télécharger PDF'}
          </Button>
        </div>
      </header>

      {/* Contenu de l'article */}
      <div className="article-content">
        {formatContent(article.fullContent || article.content)}
      </div>

      {/* CTA intermédiaire */}
      <div className="my-10 p-6 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl text-white">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2">Besoin d'aller plus loin ?</h3>
          <p className="text-blue-100 mb-4">
            Utilisez nos simulateurs gratuits pour calculer précisément vos revenus et charges.
          </p>
          <Button 
            className="bg-white text-blue-600 hover:bg-gray-100"
            onClick={() => window.location.href = '/simulateurs'}
          >
            Accéder aux simulateurs
          </Button>
        </div>
      </div>

      {/* Footer de l'article */}
      <footer className="mt-12 pt-8 border-t border-gray-200">
        <div className="bg-gray-50 p-6 rounded-xl">
          <div className="flex items-start gap-4">
            <img 
              src="/logo-bozzam.jpg" 
              alt="Bozzam" 
              className="w-16 h-auto"
            />
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-lg mb-2">
                Cet article vous a été utile ?
              </h3>
              <p className="text-gray-600 mb-4">
                Découvrez l'application Bozzam : l'outil tout-en-un des freelances pour gérer votre activité, 
                trouver des missions et optimiser votre fiscalité.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
                >
                  Découvrir Bozzam
                </Button>
                <Button 
                  variant="outline"
                  onClick={shareArticle}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Partager
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center text-sm text-gray-500 mt-6">
          <p>© 2026 Bozzam - Tous droits réservés</p>
        </div>
      </footer>
    </article>
  );
};

export default ArticleContent;
